import React from "react";
import { Button, Spinner } from "react-bootstrap";
import { LuRefreshCw, LuPlus, LuDownload, LuUpload } from "react-icons/lu";
import WidgetBase from "../common/widgetBase";
import { Converter } from "../../workspace/converter";
import GraphCanvas from "./canvas";
import GraphDetailsPanel, { propsToOperations } from "./details";
import GraphCreateDialog from "./createDialog";
import Tooltip from "../../../controls/common/tooltip";
import { createStore, mergePage, applyChanges, diffIds } from "./model";
import { elementsToInterchange, interchangeToOperations } from "./interchange";
import "./index.css";

// Maximum number of elements the canvas will render before we warn the user
// to switch to subset mode. Prevents locking up the browser with very large
// graphs loaded all at once.
const GRAPH_RENDER_BUDGET = 2000;

class GraphNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      // Array of elements fed to <GraphCanvas>. Derived from graphStore on
      // each load / expand so React Flow gets a stable reference to compare.
      elements: [],
      // In-memory element store (model.js). Keyed by element_id.
      graphStore: createStore(),
      // Generic loading flag while an API call is in flight.
      loading: false,
      loadError: null,
      // Opaque cursor for the next page in complete mode (null = done).
      graphCursor: null,
      // Per-vertex cursors for subset-mode neighbor expansion.
      // { [element_id]: cursor_string | null }
      neighborCursors: {},
      // Set to true when complete-mode load hits GRAPH_RENDER_BUDGET.
      graphTooLarge: false,
      // Currently selected element (vertex or edge object), or null.
      selected: null,
      // Create-dialog state: which type of element is being created (null = closed),
      // and the pending React Flow connection awaiting a label.
      createMode: null,
      pendingConnection: null,
    };

    // Keyed comparison guard: avoid redundant loads on irrelevant re-renders.
    this._lastLoadKey = null;
    this.mounted = false;

    // Live-sync subscription tracking — mirrors ThreadNode.boundAlias/boundCallback.
    // Tracks which alias we're currently subscribed to so we can unsubscribe when
    // the alias changes or the component unmounts.
    this._subscribedAlias = null;
    this._graphChangedCallback = null;

    // Reconcile floor timer (low-frequency, cleared on unmount).
    this._reconcileTimer = null;

    // Ref for the hidden file input used by the import action.
    this.fileInputRef = React.createRef();
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;
    this.checkReload();
    this._startReconcileTimer();
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    this.checkReload();
    this._syncGraphSubscription();
  }

  componentWillUnmount() {
    this.mounted = false;
    this._unsubscribeFromGraphEvents();
    this._stopReconcileTimer();
    super.componentWillUnmount();
  }

  // ---------------------------------------------------------------------------
  // Graph event subscription — mirrors ThreadNode.subscribeToThreadEvents()
  // ---------------------------------------------------------------------------

  /** Subscribe to graph-changed events for the currently bound alias. */
  _subscribeToGraphEvents() {
    this._unsubscribeFromGraphEvents();
    const alias = this.getBoundAlias();
    if ( !alias || !this.context ) return;
    this._graphChangedCallback = (payload) => this.onGraphChanged(payload);
    this._subscribedAlias = alias;
    this.context.addGraphEventListener(alias, this._graphChangedCallback);
  }

  /** Unsubscribe from graph-changed events (safe to call when not subscribed). */
  _unsubscribeFromGraphEvents() {
    if ( this._subscribedAlias && this._graphChangedCallback && this.context ) {
      this.context.removeGraphEventListener(this._subscribedAlias, this._graphChangedCallback);
    }
    this._subscribedAlias = null;
    this._graphChangedCallback = null;
  }

  /**
   * Re-subscribe when the bound alias changes (called from componentDidUpdate).
   * No-ops when the alias hasn't changed.
   */
  _syncGraphSubscription() {
    const alias = this.getBoundAlias();
    if ( alias === this._subscribedAlias ) return;
    this._subscribeToGraphEvents();
  }

  // ---------------------------------------------------------------------------
  // Reconcile floor timer
  // ---------------------------------------------------------------------------

  _startReconcileTimer() {
    this._stopReconcileTimer();
    const tick = () => {
      if ( !this.mounted ) return;
      this.reconcile().catch(() => {
        // Reconcile failures are best-effort; the next tick will retry.
      });
      this._reconcileTimer = setTimeout(tick, 30000);
    };
    this._reconcileTimer = setTimeout(tick, 30000);
  }

  _stopReconcileTimer() {
    if ( this._reconcileTimer !== null ) {
      clearTimeout(this._reconcileTimer);
      this._reconcileTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Accessors — mirror ThreadNode exactly
  // ---------------------------------------------------------------------------

  getApi() {
    return this.props.data.shellInterface.getApp().api;
  }

  getRevisionId() {
    return this.props.data.shellInterface.getRevision()["id"];
  }

  getSessionId() {
    return this.context && this.context.session ? this.context.session.id : null;
  }

  getBoundAlias() {
    return Converter.getComponentPortValueData(this.props.data.component, "graph");
  }

  getStartMode() {
    return Converter.getComponentPortValueData(this.props.data.component, "start_mode") || "complete";
  }

  getSeed() {
    return Converter.getComponentPortValueData(this.props.data.component, "seed") || "";
  }

  getExpandDirection() {
    return Converter.getComponentPortValueData(this.props.data.component, "expand_direction") || "both";
  }

  // Whether the viewing user may mutate this graph — gates every editing control. Mirrors the playground's
  // canEditRevision (write access + draft/managed phase). Read-only viewers can still pan, select, view
  // details, refresh, and expand neighborhoods — all read-only operations.
  canEdit() {
    const si = this.props.data && this.props.data.shellInterface;
    return !!(si && si.canEditRevision && si.canEditRevision());
  }

  // ---------------------------------------------------------------------------
  // Envelope state — mirror ThreadNode.getEnvelopeState() verbatim
  // ---------------------------------------------------------------------------

  // Inspect session-manager state to decide what we can render.
  // Returns one of:
  //   { status: "unbound" }       — no graph port binding
  //   { status: "no-session" }    — alias set, no session selected
  //   { status: "loading" }       — session-objects list or value still loading
  //   { status: "missing" }       — list loaded, no object with this alias
  //   { status: "wrong-type" }    — object exists but value.type !== "graph"
  //   { status: "ready", value }  — object exists and is a graph
  //
  // Also registers the alias on discoveredAliases so WidgetBase re-renders
  // the widget when the value becomes available.
  getEnvelopeState() {
    const alias = this.getBoundAlias();
    if ( !alias ) return { status: "unbound" };

    const manager = this.context;
    if ( !manager || !manager.session ) return { status: "no-session" };

    // Subscribe so this widget re-renders when the value arrives.
    // Cleared per-render by WidgetBase.updateSubscriptions().
    this.discoveredAliases.add(alias);

    const sessionObjects = manager.sessionObjects;
    if ( sessionObjects === null || sessionObjects === undefined ) {
      return { status: "loading", reason: "Loading session objects..." };
    }

    const exists = sessionObjects.some(o => o.alias === alias);
    if ( !exists ) {
      return { status: "missing", alias };
    }

    const value = manager.getSessionData ? manager.getSessionData(alias) : undefined;
    if ( value === undefined || value === null ) {
      return { status: "loading", reason: "Loading graph..." };
    }

    if ( value.type !== "graph" ) {
      return { status: "wrong-type", alias, type: value.type || "unknown" };
    }

    return { status: "ready", value };
  }

  // ---------------------------------------------------------------------------
  // Reload gate — fire initial load when alias/session/readiness changes
  // ---------------------------------------------------------------------------

  checkReload() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const ready = this.getEnvelopeState().status === "ready";
    const mode = this.getStartMode();
    // Key on everything that determines WHAT is loaded so changing any of it reloads the graph: alias,
    // session, readiness, the start mode (so switching complete<>subset reloads), and — in subset mode —
    // the seed and expand direction.
    const subsetKey = mode === "subset" ? `${this.getSeed()}|${this.getExpandDirection()}` : "";
    const key = `${alias || ""}|${sessionId || ""}|${ready ? "ready" : "wait"}|${mode}|${subsetKey}`;
    if ( key === this._lastLoadKey ) return;
    this._lastLoadKey = key;

    if ( !alias || !sessionId || !ready ) {
      // Reset state when we can't load.
      this._unsubscribeFromGraphEvents();
      this.setState({
        elements: [],
        graphStore: createStore(),
        loading: false,
        loadError: null,
        graphCursor: null,
        neighborCursors: {},
        graphTooLarge: false,
      });
      return;
    }

    // Subscribe to live delta events for this alias (no-ops if already subscribed).
    this._subscribeToGraphEvents();

    if ( mode === "subset" ) {
      this.loadSubset();
    } else {
      this.loadComplete();
    }
  }

  // ---------------------------------------------------------------------------
  // complete mode — page through all elements
  // ---------------------------------------------------------------------------

  async loadComplete() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const api = this.getApi();

    this.setState({
      loading: true,
      loadError: null,
      graphTooLarge: false,
      graphStore: createStore(),
      elements: [],
      graphCursor: null,
    });

    let store = createStore();
    let cursor = null;

    try {
      while ( true ) {
        const response = await api.listSessionGraphElements(revisionId, sessionId, alias, { cursor });
        if ( !this.mounted ) return;
        // Stale-alias guard: alias or session changed while we were in flight.
        if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

        const data = (response && response.data) || {};
        const pageElements = data.elements || [];
        const nextCursor = data.cursor || null;

        store = mergePage(store, pageElements);

        const totalCount = Object.keys(store.byId).length;
        if ( totalCount > GRAPH_RENDER_BUDGET ) {
          // Graph is too large to render all at once. Surface a notice.
          this.setState({
            graphStore: store,
            elements: Object.values(store.byId),
            loading: false,
            graphTooLarge: true,
            graphCursor: nextCursor,
          });
          return;
        }

        if ( !nextCursor ) {
          // Last page — we're done.
          this.setState({
            graphStore: store,
            elements: Object.values(store.byId),
            loading: false,
            graphCursor: null,
          });
          return;
        }

        cursor = nextCursor;
      }
    } catch (err) {
      if ( !this.mounted ) return;
      this.setState({
        loading: false,
        loadError: err && err.message ? err.message : "Failed to load graph elements.",
      });
    }
  }

  // ---------------------------------------------------------------------------
  // subset mode — load neighborhood of seed vertices
  // ---------------------------------------------------------------------------

  async loadSubset() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const rawSeed = this.getSeed();
    const seedIds = rawSeed
      .split(",")
      .map(s => s.trim())
      .filter(s => s.length > 0);

    if ( seedIds.length === 0 ) {
      // No seed configured — show the missing-seed warning; nothing to load.
      this.setState({ loading: false, loadError: null });
      return;
    }

    const api = this.getApi();
    const direction = this.getExpandDirection();

    this.setState({
      loading: true,
      loadError: null,
      graphStore: createStore(),
      elements: [],
      graphCursor: null,
      neighborCursors: {},
    });

    try {
      const [neighborsResp, seedsResp] = await Promise.all([
        api.listSessionGraphNeighbors(revisionId, sessionId, alias, seedIds, { direction }),
        api.getSessionGraphElements(revisionId, sessionId, alias, seedIds),
      ]);

      if ( !this.mounted ) return;
      if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

      const neighborData = (neighborsResp && neighborsResp.data) || {};
      const edges = neighborData.edges || [];
      const vertices = neighborData.vertices || [];
      const neighborCursor = neighborData.cursor || null;

      const seedData = (seedsResp && seedsResp.data) || {};
      const seedElements = seedData.elements || [];

      let store = createStore();
      store = mergePage(store, seedElements);
      store = mergePage(store, vertices);
      store = mergePage(store, edges);

      // Record per-vertex cursors so expand-more works and frontier vs fully-loaded is known.
      // Single seed: the returned cursor belongs to it — record it even when null, which marks the
      // seed as fully loaded (no more neighbor pages). Multiple seeds: the server returns one shared
      // cursor that cannot be paged or attributed per seed, so leave the seeds unrecorded (frontier)
      // — the user can expand each individually to page through its neighbors.
      const nextNeighborCursors = {};
      if ( seedIds.length === 1 ) {
        nextNeighborCursors[seedIds[0]] = neighborCursor;
      } else if ( neighborCursor ) {
        for ( const id of seedIds ) {
          nextNeighborCursors[id] = neighborCursor;
        }
      }

      this.setState({
        graphStore: store,
        elements: Object.values(store.byId),
        loading: false,
        neighborCursors: nextNeighborCursors,
      });
    } catch (err) {
      if ( !this.mounted ) return;
      this.setState({
        loading: false,
        loadError: err && err.message ? err.message : "Failed to load graph neighborhood.",
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Neighbor expansion — triggered on node double-click
  // ---------------------------------------------------------------------------

  async loadNeighbors(elementId) {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const api = this.getApi();
    const direction = this.getExpandDirection();
    const existingCursor = this.state.neighborCursors[elementId] || null;

    try {
      const response = await api.listSessionGraphNeighbors(
        revisionId, sessionId, alias, [elementId],
        { direction, cursor: existingCursor || undefined }
      );

      if ( !this.mounted ) return;
      if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

      const data = (response && response.data) || {};
      const edges = data.edges || [];
      const vertices = data.vertices || [];
      const nextCursor = data.cursor || null;

      this.setState((prev) => {
        let store = mergePage(prev.graphStore, vertices);
        store = mergePage(store, edges);
        return {
          graphStore: store,
          elements: Object.values(store.byId),
          neighborCursors: {
            ...prev.neighborCursors,
            [elementId]: nextCursor,
          },
        };
      });
    } catch (err) {
      if ( !this.mounted ) return;
      // Expansion errors are best-effort; surface quietly.
      this.setState({
        loadError: err && err.message ? err.message : "Failed to load neighbors.",
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Incremental live sync — process deltas from session_graph_changed events
  // ---------------------------------------------------------------------------

  /**
   * Receive a graph-changed delta from the SessionManager and apply it incrementally.
   *
   * Steps:
   *   1. Ignore if the event alias doesn't match the currently bound alias.
   *   2. In subset mode, filter out upserts for element ids not in the loaded working set
   *      (don't expand the working set via events).
   *   3. Run applyChanges(store, changes, {}) to apply deletes and identify which upsert
   *      ids need fetching.
   *   4. Batch-fetch needsFetch ids via getSessionGraphElements (one call).
   *   5. Run applyChanges again with the fetched data to merge updates.
   *   6. setState with the new store.
   *
   * @param {{ alias: string, changes: Array }} payload
   */
  async onGraphChanged(payload) {
    if ( !payload ) return;
    const alias = this.getBoundAlias();
    if ( payload.alias !== alias ) return;

    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const changes = payload.changes || [];
    if ( changes.length === 0 ) return;

    const isSubset = this.getStartMode() === "subset";
    const currentStore = this.state.graphStore;

    // In subset mode: filter upserts for ids outside the loaded working set.
    // Deletes are always applied (they shrink the store, never expand it).
    const filteredChanges = isSubset
      ? changes.filter(c => {
        if ( c.op === "delete" ) return true;                      // always apply deletes
        return currentStore.byId[c.element_id] !== undefined;     // only upsert if in working set
      })
      : changes;

    if ( filteredChanges.length === 0 ) return;

    // First pass: apply with empty fetchedById to compute needsFetch.
    const { store: storeAfterDeletes, needsFetch } = applyChanges(currentStore, filteredChanges, {});

    if ( needsFetch.length === 0 ) {
      // Nothing to fetch — deletes only (or all upserts were at the same rev).
      if ( !this.mounted ) return;
      this.setState({
        graphStore: storeAfterDeletes,
        elements: Object.values(storeAfterDeletes.byId),
      });
      return;
    }

    // Batch-fetch the ids that need updating.
    const api = this.getApi();
    let fetchedById = {};
    try {
      const response = await api.getSessionGraphElements(revisionId, sessionId, alias, needsFetch);
      if ( !this.mounted ) return;
      const elements = (response && response.data && response.data.elements) || [];
      for ( const el of elements ) {
        fetchedById[el.element_id] = el;
      }
    } catch (_err) {
      // Fetch failure is best-effort; apply what we have (deletes already done).
      if ( !this.mounted ) return;
      this.setState({
        graphStore: storeAfterDeletes,
        elements: Object.values(storeAfterDeletes.byId),
      });
      return;
    }

    // Second pass: apply with fetched data to merge upserts.
    const { store: finalStore } = applyChanges(currentStore, filteredChanges, fetchedById);

    if ( !this.mounted ) return;
    this.setState({
      graphStore: finalStore,
      elements: Object.values(finalStore.byId),
    });
  }

  // ---------------------------------------------------------------------------
  // Reconcile floor — gap recovery (call on load completion and via timer)
  // ---------------------------------------------------------------------------

  /**
   * Reconcile the local element store against the server's current state.
   *
   * Complete mode:
   *   Page listSessionGraphElements({ids_only:true}) to collect the server id-set,
   *   then diff against the local store. Remove deletedLocally entries; fetch
   *   changedOrNew entries via getSessionGraphElements and merge.
   *
   * Subset mode:
   *   Call getSessionGraphElements(all working-set ids). Any requested id the server
   *   omits has been deleted — remove it locally.
   */
  async reconcile() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;
    if ( !this.mounted ) return;

    const api = this.getApi();
    const isSubset = this.getStartMode() === "subset";
    const currentStore = this.state.graphStore;

    if ( isSubset ) {
      // Subset mode: fetch all working-set ids; absent ones were deleted.
      const workingIds = Object.keys(currentStore.byId);
      if ( workingIds.length === 0 ) return;

      let response;
      try {
        response = await api.getSessionGraphElements(revisionId, sessionId, alias, workingIds);
      } catch (_err) {
        return; // Best-effort.
      }
      if ( !this.mounted ) return;
      if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

      const returnedElements = (response && response.data && response.data.elements) || [];
      const returnedIds = new Set(returnedElements.map(el => el.element_id));

      // Build the reconciled store: drop missing ids (deleted on server).
      const byId = { ...currentStore.byId };
      for ( const id of workingIds ) {
        if ( !returnedIds.has(id) ) {
          delete byId[id];
        }
      }
      // Merge updated elements.
      for ( const el of returnedElements ) {
        const existing = byId[el.element_id];
        if ( !existing || el.rev > existing.rev ) {
          byId[el.element_id] = JSON.parse(JSON.stringify(el));
        }
      }

      this.setState({
        graphStore: { byId },
        elements: Object.values(byId),
      });
    } else {
      // Complete mode: page ids_only list to get the full server id-set.
      let cursor = null;
      const allServerIds = [];
      try {
        while ( true ) {
          const response = await api.listSessionGraphElements(
            revisionId, sessionId, alias, { ids_only: true, cursor }
          );
          if ( !this.mounted ) return;
          if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

          const data = (response && response.data) || {};
          const pageIds = data.element_ids || [];
          allServerIds.push(...pageIds);
          cursor = data.cursor || null;
          if ( !cursor ) break;
        }
      } catch (_err) {
        return; // Best-effort.
      }

      if ( !this.mounted ) return;

      // Use diffIds to find what changed.
      const { deletedLocally, changedOrNew } = diffIds(currentStore, allServerIds);

      // Apply deletes first (synchronous).
      let reconciledStore = currentStore;
      if ( deletedLocally.length > 0 ) {
        const byId = { ...reconciledStore.byId };
        for ( const id of deletedLocally ) {
          delete byId[id];
        }
        reconciledStore = { byId };
      }

      // Fetch changedOrNew ids and merge.
      if ( changedOrNew.length > 0 ) {
        let fetchedElements = [];
        try {
          const response = await api.getSessionGraphElements(revisionId, sessionId, alias, changedOrNew);
          if ( !this.mounted ) return;
          if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;
          fetchedElements = (response && response.data && response.data.elements) || [];
        } catch (_err) {
          // Partial reconcile — apply deletes but not updates.
        }

        const byId = { ...reconciledStore.byId };
        for ( const el of fetchedElements ) {
          const existing = byId[el.element_id];
          if ( !existing || el.rev > existing.rev ) {
            byId[el.element_id] = JSON.parse(JSON.stringify(el));
          }
        }
        reconciledStore = { byId };
      }

      if ( !this.mounted ) return;
      this.setState({
        graphStore: reconciledStore,
        elements: Object.values(reconciledStore.byId),
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Mutations — apply a batch of graph mutation operations
  // ---------------------------------------------------------------------------

  /**
   * Apply a batch of mutation operations optimistically, then reconcile with
   * the server response.
   *
   * Flow:
   *   1. Take a pre-mutation snapshot of the store for rollback.
   *   2. Optimistically apply each op to a local copy of the store and setState
   *      so the canvas updates immediately.
   *   3. Call api.applySessionGraphMutations; on success reconcile the store
   *      from the response { elements, changes } (authoritative rev/element_id).
   *   4a. On graph_mutation_conflict (409): surface a conflict notice, reload the
   *       affected element(s) via getSessionGraphElements, and roll back.
   *   4b. On other errors: roll back + show an error notice.
   *
   * @param {Array} operations - mutation operation objects (add_vertex, add_edge,
   *   delete_vertex, delete_edge, set_*_props, remove_*_props)
   */
  async applyMutations(operations) {
    // Read-only viewers cannot mutate. This is the single choke point for every graph mutation (add/delete
    // vertex & edge, property edits, import), so guarding here is the master safety net behind the hidden
    // controls.
    if ( !this.canEdit() ) return;
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const api = this.getApi();

    // 1. Snapshot current store for rollback.
    const snapshotStore = { byId: { ...this.state.graphStore.byId } };

    // 2. Optimistic local apply.
    const optimisticStore = this._applyOpsLocally(snapshotStore, operations);
    this.setState({
      graphStore: optimisticStore,
      elements: Object.values(optimisticStore.byId),
      mutationError: null,
    });

    try {
      // 3. Call the API.
      const response = await api.applySessionGraphMutations(revisionId, sessionId, alias, operations);
      if ( !this.mounted ) return;

      // Reconcile from server response.
      const data = (response && response.data) || {};
      const serverElements = data.elements || [];
      const changes = data.changes || [];

      this.setState((prev) => {
        // Start from current optimistic store and apply authoritative data.
        let store = { byId: { ...prev.graphStore.byId } };
        store = this._reconcileFromServer(store, serverElements, changes);
        return {
          graphStore: store,
          elements: Object.values(store.byId),
          mutationError: null,
        };
      });
    } catch (err) {
      if ( !this.mounted ) return;

      // Detect conflict: axios wraps 4xx in error.response
      const errorCode =
        err &&
        err.response &&
        err.response.data &&
        err.response.data.error;

      if ( errorCode === "graph_mutation_conflict" ) {
        // 4a. Conflict — surface notice, reload affected elements, roll back.
        const affectedIds = this._extractElementIdsFromOps(operations);

        // Roll back first so the UI doesn't show stale optimistic state.
        this.setState({
          graphStore: snapshotStore,
          elements: Object.values(snapshotStore.byId),
          mutationError: "Edit conflict — the graph was modified by another session. Refreshing affected elements.",
        });

        try {
          if ( affectedIds.length > 0 ) {
            const reloadResp = await api.getSessionGraphElements(revisionId, sessionId, alias, affectedIds);
            if ( !this.mounted ) return;
            const reloadData = (reloadResp && reloadResp.data) || {};
            const reloadedElements = reloadData.elements || [];
            if ( reloadedElements.length > 0 ) {
              this.setState((prev) => {
                let store = { byId: { ...prev.graphStore.byId } };
                for ( const el of reloadedElements ) {
                  store.byId[el.element_id] = JSON.parse(JSON.stringify(el));
                }
                return {
                  graphStore: store,
                  elements: Object.values(store.byId),
                };
              });
            }
          }
        } catch (_reloadErr) {
          // Reload failure is best-effort; the rollback is already applied.
        }
      } else {
        // 4b. Generic error — roll back + show error.
        this.setState({
          graphStore: snapshotStore,
          elements: Object.values(snapshotStore.byId),
          mutationError: err && err.message ? err.message : "Failed to apply graph mutation.",
        });
      }
    }
  }

  /**
   * Apply mutation operations locally to produce an optimistic store.
   * Returns a new store object; does not mutate the input.
   *
   * @param {{ byId: Object }} store
   * @param {Array} operations
   * @returns {{ byId: Object }}
   */
  _applyOpsLocally(store, operations) {
    const byId = { ...store.byId };

    for ( const op of operations ) {
      if ( op.op === "add_vertex" ) {
        // Use a temporary local id until the server assigns a permanent one.
        const tempId = op.element_id || `__local_${Date.now()}_${Math.random()}`;
        byId[tempId] = {
          element_id: tempId,
          type: "vertex",
          labels: op.labels || [],
          props: op.props || {},
          rev: 0,
          _optimistic: true,
        };
      } else if ( op.op === "add_edge" ) {
        const tempId = op.element_id || `__local_${Date.now()}_${Math.random()}`;
        byId[tempId] = {
          element_id: tempId,
          type: "edge",
          label: op.label || "edge",
          from_id: op.from_id,
          to_id: op.to_id,
          props: op.props || {},
          rev: 0,
          _optimistic: true,
        };
      } else if ( op.op === "delete_vertex" ) {
        // Remove vertex and all incident edges.
        const id = op.element_id;
        delete byId[id];
        for ( const eid of Object.keys(byId) ) {
          const el = byId[eid];
          if ( el.type === "edge" && (el.from_id === id || el.to_id === id) ) {
            delete byId[eid];
          }
        }
      } else if ( op.op === "delete_edge" ) {
        delete byId[op.element_id];
      } else if ( op.op === "set_vertex_props" || op.op === "set_edge_props" ) {
        const el = byId[op.element_id];
        if ( el ) {
          byId[op.element_id] = { ...el, props: { ...el.props, ...op.props } };
        }
      } else if ( op.op === "remove_vertex_props" || op.op === "remove_edge_props" ) {
        const el = byId[op.element_id];
        if ( el ) {
          const props = { ...el.props };
          for ( const key of (op.keys || []) ) {
            delete props[key];
          }
          byId[op.element_id] = { ...el, props };
        }
      }
    }

    return { byId };
  }

  /**
   * Reconcile the local store against the server's authoritative response.
   * - Removes elements the server confirmed deleted.
   * - Upserts elements returned in the `elements` array (authoritative rev).
   * - Strips optimistic placeholders whose server counterpart has arrived.
   *
   * @param {{ byId: Object }} store
   * @param {Array} serverElements - authoritative element objects from server
   * @param {Array} changes - [{ op: 'upsert'|'delete', element_id, type, rev }]
   * @returns {{ byId: Object }}
   */
  _reconcileFromServer(store, serverElements, changes) {
    const byId = { ...store.byId };

    // Build a lookup of server-returned elements by element_id.
    const serverById = {};
    for ( const el of serverElements ) {
      serverById[el.element_id] = el;
    }

    // Remove all optimistic placeholders — they'll be replaced by server ids.
    for ( const id of Object.keys(byId) ) {
      if ( byId[id]._optimistic ) {
        delete byId[id];
      }
    }

    // Apply changes from the server.
    for ( const change of changes ) {
      if ( change.op === "delete" ) {
        if ( change.type === "vertex" ) {
          // Remove incident edges.
          for ( const eid of Object.keys(byId) ) {
            const el = byId[eid];
            if ( el.type === "edge" && (el.from_id === change.element_id || el.to_id === change.element_id) ) {
              delete byId[eid];
            }
          }
        }
        delete byId[change.element_id];
      } else if ( change.op === "upsert" ) {
        if ( serverById[change.element_id] ) {
          byId[change.element_id] = JSON.parse(JSON.stringify(serverById[change.element_id]));
        }
      }
    }

    return { byId };
  }

  /**
   * Extract the element_ids referenced in mutation operations (for conflict reload).
   *
   * @param {Array} operations
   * @returns {string[]}
   */
  _extractElementIdsFromOps(operations) {
    const ids = new Set();
    for ( const op of operations ) {
      if ( op.element_id ) ids.add(op.element_id);
      if ( op.from_id ) ids.add(op.from_id);
      if ( op.to_id ) ids.add(op.to_id);
    }
    return [...ids];
  }

  // ---------------------------------------------------------------------------
  // Import / Export — graph JSON interchange (Task 10)
  // ---------------------------------------------------------------------------

  /**
   * Split an operations array into chunks of at most `size` entries, preserving order.
   *
   * @param {Array} operations
   * @param {number} size - maximum chunk size (default 30)
   * @returns {Array[]} - array of chunks
   */
  static chunkOperations(operations, size = 30) {
    const chunks = [];
    for (let i = 0; i < operations.length; i += size) {
      chunks.push(operations.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Build the JSON string that represents the current graph store in interchange format.
   * Callers can spy on this in tests without dealing with DOM downloads.
   *
   * @returns {string} - pretty-printed JSON interchange string
   */
  buildExportBlobData() {
    const elements = Object.values(this.state.graphStore.byId);
    const interchange = elementsToInterchange(elements);
    return JSON.stringify(interchange, null, 2);
  }

  /**
   * Trigger a file-download of the current graph as interchange JSON.
   * Uses the anchor+Blob pattern (same as graphs/dnn/shell).
   */
  exportGraph() {
    const alias = this.getBoundAlias() || "graph";
    const json = this.buildExportBlobData();
    const blob = new Blob([json], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    document.body.appendChild(a);
    a.href = url;
    a.setAttribute("download", `graph-${alias}.json`);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Apply an interchange JSON object to the current session graph.
   *
   * Operations are derived via interchangeToOperations (vertices first, then edges),
   * split into chunks of ≤30, and applied sequentially via applyMutations.
   * If a chunk fails, an error is shown and remaining chunks are skipped.
   * The file input ref is always reset after completion.
   *
   * @param {Object} json - interchange JSON object { vertices: [...], edges: [...] }
   */
  async importInterchange(json) {
    this.setState({ importError: null });

    let operations;
    try {
      operations = interchangeToOperations(json);
    } catch (err) {
      this.setState({ importError: err && err.message ? err.message : "Invalid interchange JSON." });
      this._resetFileInput();
      return;
    }

    const chunks = GraphNode.chunkOperations(operations);

    try {
      for (let i = 0; i < chunks.length; i++) {
        try {
          await this.applyMutations(chunks[i]);
        } catch (err) {
          const chunkNum = i + 1;
          const msg = err && err.message ? err.message : "Unknown error";
          this.setState({
            importError: `Import failed on chunk ${chunkNum} of ${chunks.length}: ${msg}. Prior chunks were applied.`,
          });
          return;
        }
      }
    } finally {
      this._resetFileInput();
    }
  }

  /**
   * Handle the hidden file input's onChange event: read the file, parse JSON,
   * then delegate to importInterchange.
   *
   * @param {Event} event
   */
  handleImportFileChange(event) {
    if (!event.target.files || event.target.files.length !== 1) return;
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      let json;
      try {
        json = JSON.parse(e.target.result);
      } catch (parseErr) {
        this.setState({ importError: "Failed to parse JSON file." });
        this._resetFileInput();
        return;
      }
      this.importInterchange(json);
    };
    reader.readAsText(file);
  }

  /** Reset the hidden file input value so the same file can be re-imported. */
  _resetFileInput() {
    if (this.fileInputRef && this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  }

  // ---------------------------------------------------------------------------
  // Refresh — clear store and re-run baseline load
  // ---------------------------------------------------------------------------

  async refresh() {
    this.setState({
      graphStore: createStore(),
      elements: [],
      graphCursor: null,
      neighborCursors: {},
      graphTooLarge: false,
      loadError: null,
      // Clear the selected vertex/edge (and close the details panel) — it may not exist after reload.
      selected: null,
    });

    const mode = this.getStartMode();
    if ( mode === "subset" ) {
      await this.loadSubset();
    } else {
      await this.loadComplete();
    }
  }

  // ---------------------------------------------------------------------------
  // Selection — used by GraphCanvas.onSelect and the details panel
  // ---------------------------------------------------------------------------

  /**
   * Called when the user clicks a node or edge on the canvas.
   * Passing null clears the selection (pane click).
   *
   * @param {Object|null} element
   */
  handleSelect(element) {
    // When re-selecting the same element, keep the panel open so the user can
    // keep editing. The store may have been refreshed since the last click, so
    // always grab the freshest copy from the store by element_id.
    if (element && element.element_id) {
      const fresh = this.state.graphStore.byId[element.element_id];
      this.setState({ selected: fresh || element });
    } else {
      this.setState({ selected: null });
    }
  }

  // ---------------------------------------------------------------------------
  // Create flow — specify labels / label at creation time
  // ---------------------------------------------------------------------------

  static buildAddVertexOp(labels, props) {
    return { op: "add_vertex", labels: labels || [], props: props || {} };
  }

  static buildAddEdgeOp(connection, label) {
    return {
      op: "add_edge",
      label: label,
      from_id: connection.source,
      to_id: connection.target,
      props: {},
    };
  }

  // Distinct vertex labels and edge labels present in the visible graph — used to populate the create
  // dialog's editable dropdowns.
  static collectLabels(elements) {
    const vertexSet = new Set();
    const edgeSet = new Set();
    for (const el of (elements || [])) {
      if (el.type === "vertex" && Array.isArray(el.labels)) {
        for (const label of el.labels) {
          if (label) vertexSet.add(label);
        }
      } else if (el.type === "edge" && el.label) {
        edgeSet.add(el.label);
      }
    }
    return {
      vertexLabels: [...vertexSet].sort(),
      edgeLabels: [...edgeSet].sort(),
    };
  }

  openCreateVertex() {
    this.setState({ createMode: "vertex", pendingConnection: null });
  }

  handleConnect(connection) {
    if ( !this.canEdit() ) return;
    this.setState({ createMode: "edge", pendingConnection: connection });
  }

  closeCreate() {
    this.setState({ createMode: null, pendingConnection: null });
  }

  handleCreateSubmit(payload) {
    const { createMode, pendingConnection } = this.state;
    if (createMode === "vertex") {
      this.applyMutations([GraphNode.buildAddVertexOp(payload.labels, payload.props)]);
    } else if (createMode === "edge" && pendingConnection) {
      this.applyMutations([GraphNode.buildAddEdgeOp(pendingConnection, payload.label)]);
    }
    this.closeCreate();
  }

  // ---------------------------------------------------------------------------
  // WidgetBase overrides
  // ---------------------------------------------------------------------------

  getClassName() {
    return "playground-graph-node";
  }

  renderHeaderAlias() {
    const alias = this.getBoundAlias();
    if ( !alias ) return null;
    return <span className="playground-widget-alias">{alias}</span>;
  }

  renderHeaderActions() {
    const envelope = this.getEnvelopeState();
    if ( envelope.status !== "ready" ) return null;

    return (
      <span className="playground-widget-controls" onClick={(e) => e.stopPropagation()}>
        {this.canEdit() &&
          <Tooltip text="Add vertex">
            <Button
              variant="light"
              size="sm"
              onClick={() => this.openCreateVertex()}
              disabled={this.state.loading}
            >
              <LuPlus />
            </Button>
          </Tooltip>
        }
        <Tooltip text="Refresh graph">
          <Button
            variant="light"
            size="sm"
            onClick={() => this.refresh()}
            disabled={this.state.loading}
          >
            <LuRefreshCw />
          </Button>
        </Tooltip>
      </span>
    );
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();
    const envelopeState = this.getEnvelopeState();
    const isSubsetMode = this.getStartMode() === "subset";
    const seedIsEmpty = isSubsetMode && !(this.getSeed() || "").trim();

    return (
      <div className="playground-graph-node" style={{ width, height, maxHeight }}>
        {envelopeState.status === "unbound" &&
          this.renderNotice({ variant: "info", message: 'Connect a value to the "graph" port.' })
        }
        {envelopeState.status === "no-session" &&
          this.renderNotice({ variant: "info", message: "Select a session to view this widget." })
        }
        {envelopeState.status === "loading" &&
          this.renderLoading(envelopeState.reason || "Loading…")
        }
        {envelopeState.status === "missing" &&
          this.renderNotice({
            variant: "info",
            message: `"${envelopeState.alias}" is not available in the selected session yet.`,
          })
        }
        {envelopeState.status === "wrong-type" &&
          this.renderNotice({
            variant: "warning",
            message: `The "graph" port expects a "graph" value, but "${envelopeState.alias}" is "${envelopeState.type}".`,
          })
        }
        {envelopeState.status === "ready" && this.state.loading &&
          <div className="playground-graph-empty-state">
            <Spinner animation="border" role="status" className="playground-graph-loading-icon" />
            <p className="playground-graph-empty-text">Loading graph…</p>
          </div>
        }
        {envelopeState.status === "ready" && this.state.loadError && !this.state.loading &&
          this.renderNotice({ variant: "warning", message: this.state.loadError })
        }
        {envelopeState.status === "ready" && this.state.graphTooLarge && !this.state.loading &&
          this.renderNotice({
            variant: "info",
            message: (
              <>
                This graph is very large. Switch to <strong>subset</strong> mode and enter a seed vertex
                to explore a neighborhood instead.
              </>
            ),
          })
        }
        {envelopeState.status === "ready" && isSubsetMode && seedIsEmpty && !this.state.loading &&
          <div className="playground-graph-empty-state">
            <span className="playground-graph-empty-icon">&#9888;</span>
            <p className="playground-graph-empty-text">No seed vertex specified.</p>
            <p className="playground-graph-empty-hint">
              Set the <strong>seed</strong> port to one or more vertex IDs to load a subset of this graph.
            </p>
          </div>
        }
        {envelopeState.status === "ready" && !this.state.loading &&
          <>
            {/* Empty state: ready but no elements and not in subset-seed-prompt */}
            {(!isSubsetMode || !seedIsEmpty) &&
              !this.state.graphTooLarge &&
              !this.state.loadError &&
              (this.state.elements || []).length === 0 && (
              <div className="playground-graph-empty-state">
                <span className="playground-graph-empty-icon">&#9678;</span>
                <p className="playground-graph-empty-text">This graph is empty.</p>
                <p className="playground-graph-empty-hint">
                  Add a vertex using the <strong>+</strong> button above, or import a graph.
                </p>
              </div>
            )}
            {(!isSubsetMode || !seedIsEmpty) &&
              (this.state.graphTooLarge || this.state.loadError || (this.state.elements || []).length > 0) && (
              <div className="playground-graph-canvas-row">
                <div className="playground-graph-canvas nodrag">
                  <GraphCanvas
                    elements={this.state.elements || []}
                    layoutName={
                      Converter.getComponentPortValueData(this.props.data.component, "layout") || "force"
                    }
                    subsetMode={isSubsetMode}
                    neighborCursors={this.state.neighborCursors}
                    canEdit={this.canEdit()}
                    onExpand={(elementId) => this.loadNeighbors(elementId)}
                    onSelect={(element) => this.handleSelect(element)}
                    onConnect={(connection) => this.handleConnect(connection)}
                    onNodesDelete={(nodes) => this.applyMutations(
                      nodes.map(n => ({
                        op: "delete_vertex",
                        element_id: n.id,
                        if_rev: this.state.graphStore.byId[n.id]
                          ? this.state.graphStore.byId[n.id].rev
                          : undefined,
                      }))
                    )}
                    onEdgesDelete={(edges) => this.applyMutations(
                      edges.map(e => ({
                        op: "delete_edge",
                        element_id: e.id,
                        if_rev: this.state.graphStore.byId[e.id]
                          ? this.state.graphStore.byId[e.id].rev
                          : undefined,
                      }))
                    )}
                    panelOpen={!!this.state.selected}
                  />
                  {this.state.selected && (
                    <GraphDetailsPanel
                      element={
                        this.state.graphStore.byId[this.state.selected.element_id] || this.state.selected
                      }
                      timezone={
                        this.props.preferences
                          ? this.props.preferences["app.timezone"]
                          : undefined
                      }
                      onClose={() => this.setState({ selected: null })}
                      onSave={(ops) => this.applyMutations(ops)}
                      readOnly={!this.canEdit()}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        }
        <GraphCreateDialog
          show={!!this.state.createMode}
          mode={this.state.createMode}
          vertexLabels={GraphNode.collectLabels(this.state.elements).vertexLabels}
          edgeLabels={GraphNode.collectLabels(this.state.elements).edgeLabels}
          onCancel={() => this.closeCreate()}
          onCreate={(payload) => this.handleCreateSubmit(payload)}
        />
      </div>
    );
  }
}

export default GraphNode;
